import { useState, useCallback } from 'react';
import { buildQuestionnaireResponse, validateQuestionnaireResponse, QuestionnaireResponseData } from '../utils/questionnaireResponseBuilder';
import { PostQuestionnaireResponse } from '../services/fhir/patientsummary/questionnaireResponse/PostQuestionnaireResponse';
import { usePractitionerId } from '../utils/practitionerUtils';
import { toast } from 'react-toastify';
import { usePractitionerRoleContext } from '../contexts/PractitionerRoleContext';
import { useSessionContext } from '../contexts/SessionContext';

interface UseQuestionnaireResponseProps {
    patientId?: string;
    encounterId?: string;
}

interface UseQuestionnaireResponseReturn {
    loading: boolean;
    error: string | null;
    questionnaireResponse: QuestionnaireResponseData | null;

    submitQuestionnaireResponse: (
        questionnaireId: string,
        answers: Record<string, any>,
        questionnaire: any
    ) => Promise<boolean>;

    clearError: () => void;
    reset: () => void;
}


export function useQuestionnaireResponse({
    patientId,
    encounterId,
}: UseQuestionnaireResponseProps = {}): UseQuestionnaireResponseReturn {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponseData | null>(null);

    const practitionerId = usePractitionerId();
    const { userPractitionerRole, userPractitioner } = usePractitionerRoleContext();
    const { user } = useSessionContext();

    const submitQuestionnaireResponse = useCallback(async (
        questionnaireId: string,
        answers: Record<string, any>,
        questionnaire: any
    ): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const roleSpecialty = userPractitionerRole?.specialty?.[0]?.coding?.[0]?.display;
            const healthcare =
                typeof roleSpecialty === "string" &&
                roleSpecialty.trim() &&
                roleSpecialty.trim().toLowerCase() !== "undefined"
                    ? roleSpecialty
                    : '';
            const practitionerName = userPractitioner?.name?.[0]?.text ||
                userPractitioner?.name?.[0]?.family ||
                user?.username || '';

            const questionnaireResponse = await buildQuestionnaireResponse(
                questionnaireId,
                answers,
                questionnaire,
                patientId,
                encounterId,
                practitionerId || undefined,
                { healthcare, practitioner: practitionerName }
            );

            const validation = validateQuestionnaireResponse(questionnaireResponse);
            if (!validation.isValid) {
                const errorMessage = `Erros de validação: ${validation.errors.join(', ')}`;
                console.error('❌ Erros de validação:', validation.errors);
                setError(errorMessage);
                toast.error(errorMessage);
                return false;
            }

            const serverResponse = await PostQuestionnaireResponse(questionnaireResponse);

            setQuestionnaireResponse(serverResponse || questionnaireResponse);

            if (serverResponse?.id) {
                toast.success('QuestionnaireResponse enviado com sucesso');
            } else {
                toast.error('Erro ao enviar QuestionnaireResponse');
                return false;
            }

            return true;

        } catch (err: any) {
            const errorMessage = err?.message || 'Erro ao enviar questionário';
            console.error('❌ Erro ao enviar QuestionnaireResponse:', err);
            setError(errorMessage);
            toast.error(errorMessage);
            return false;
        } finally {
            setLoading(false);
        }
    }, [patientId, encounterId, practitionerId]);


    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const reset = useCallback(() => {
        setLoading(false);
        setError(null);
        setQuestionnaireResponse(null);
    }, []);

    return {
        loading,
        error,
        questionnaireResponse,

        submitQuestionnaireResponse,

        clearError,
        reset,
    };
}


export function useQuestionnaireResponses() {
    const [responses, setResponses] = useState<Record<string, QuestionnaireResponseData>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);


    const addResponse = useCallback((questionnaireId: string, response: QuestionnaireResponseData) => {
        setResponses(prev => ({
            ...prev,
            [questionnaireId]: response,
        }));
    }, []);


    const removeResponse = useCallback((questionnaireId: string) => {
        setResponses(prev => {
            const newResponses = { ...prev };
            delete newResponses[questionnaireId];
            return newResponses;
        });
    }, []);


    const clearResponses = useCallback(() => {
        setResponses({});
    }, []);


    const getResponse = useCallback((questionnaireId: string) => {
        return responses[questionnaireId] || null;
    }, [responses]);


    const getAllResponses = useCallback(() => {
        return responses;
    }, [responses]);

    return {
        responses,
        loading,
        error,

        addResponse,
        removeResponse,
        clearResponses,

        getResponse,
        getAllResponses,
    };
}

export function useQuestionnaireResponseHistory() {
    const [history, setHistory] = useState<Array<{
        id: string;
        questionnaireId: string;
        timestamp: string;
        status: 'success' | 'error';
        response?: QuestionnaireResponseData;
        error?: string;
    }>>([]);


    const addToHistory = useCallback((
        questionnaireId: string,
        status: 'success' | 'error',
        response?: QuestionnaireResponseData,
        error?: string
    ) => {
        const entry = {
            id: `${questionnaireId}-${Date.now()}`,
            questionnaireId,
            timestamp: new Date().toISOString(),
            status,
            response,
            error,
        };

        setHistory(prev => [entry, ...prev.slice(0, 49)]);
    }, []);


    const clearHistory = useCallback(() => {
        setHistory([]);
    }, []);


    const getHistoryForQuestionnaire = useCallback((questionnaireId: string) => {
        return history.filter(entry => entry.questionnaireId === questionnaireId);
    }, [history]);

    return {
        history,
        addToHistory,
        clearHistory,
        getHistoryForQuestionnaire,
    };
}
